import { useCallback } from 'react'

const SCRIPT_SRC = '//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js'

interface DaumPostcodeResult {
  zonecode: string
  roadAddress: string
  jibunAddress: string
}

export interface PostcodeSelection {
  postalCode: string
  addressBase: string
}

/** Daum/Kakao Postcode 결과 → 우편번호 + 기본주소 매핑 (순수 함수, 단위 테스트 대상) */
export function mapPostcodeResult(r: DaumPostcodeResult): PostcodeSelection {
  return {
    postalCode: r.zonecode,
    addressBase: r.roadAddress?.trim() ? r.roadAddress : r.jibunAddress,
  }
}

/** postcode.v2.js를 1회만 동적 로드 */
function loadPostcodeScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if ((window as any).daum?.Postcode) {
      resolve()
      return
    }
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`)
    if (existing) {
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', () => reject(new Error('postcode script load failed')))
      return
    }
    const script = document.createElement('script')
    script.src = SCRIPT_SRC
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('postcode script load failed'))
    document.head.appendChild(script)
  })
}

interface Props {
  onSelect: (selection: PostcodeSelection) => void
  className?: string
  label?: string
}

/** 클릭 시 Daum/Kakao 주소검색 팝업을 열고 선택 결과를 onSelect로 전달 */
export default function KakaoPostcode({ onSelect, className, label = '주소 검색' }: Props) {
  const handleClick = useCallback(async () => {
    try {
      await loadPostcodeScript()
      new (window as any).daum.Postcode({
        oncomplete: (data: DaumPostcodeResult) => onSelect(mapPostcodeResult(data)),
      }).open()
    } catch (e) {
      console.error('Kakao postcode open failed', e)
    }
  }, [onSelect])

  return (
    <button
      type="button"
      onClick={handleClick}
      className={className ?? 'px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 whitespace-nowrap'}
    >
      {label}
    </button>
  )
}
